import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, DatePicker, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UserSwitchOutlined } from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import './css/searchComponent.css';
import { useUser } from '../contexts/UserContext';

const { Search } = Input;
const { Option } = Select;

const isMobile = window.innerWidth <= 768;
const mobileSearchClass = isMobile ? 'custom-search-mobile' : '';

const parseGameDate = (value) => {
  if (!value || value === 'null' || value === 'undefined' || value.includes('NaN')) {
    return null;
  }

  if (/^\d{2}\/\d{2}\/\d{4}/.test(value)) {
    const [day, month, year, timePart] = value.split(/[\/\s]/);
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    if (timePart) {
      const [h, m, s] = timePart.split(':');
      hours = Number(h) || 0;
      minutes = Number(m) || 0;
      seconds = Number(s) || 0;
    }

    return new Date(Number(year), Number(month) - 1, Number(day), hours, minutes, seconds).getTime();
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const Games = () => {
  const [games, setGames] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50', '100'] });
  const [searchText, setSearchText] = useState('');
  const [monthYearFilter, setMonthYearFilter] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gamePlayers, setGamePlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [form] = Form.useForm();
  const user = useUser().user;

  const fetchGames = async (page = 1, search = '', pageSize = 5, monthYear = null) => {
    setLoading(true);
    try {
      const month = monthYear ? monthYear.month() + 1 : undefined;
      const year = monthYear ? monthYear.year() : undefined;
      const response = await api.get('/games', {
        params: { page, limit: pageSize, search, month, year }
      });
      setGames(response.data.data || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0, current: page, pageSize }));
    } catch (error) {
      console.error('Erro ao carregar jogos:', error);
      message.error('Erro ao carregar jogos');
      setGames([]);
    }
    setLoading(false);
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', { params: { limit: 1000 } });
      setCustomers(response.data.data);
    } catch (error) {
      message.error('Erro ao carregar times');
    }
  };

  useEffect(() => {
    fetchGames();
    fetchCustomers();
  }, []);

  const handleTableChange = (pag, filters, sorter, extra) => {
    if (extra?.action === 'sort') {
      return;
    }
    const newPage = pag.pageSize !== pagination.pageSize ? 1 : pag.current;
    fetchGames(newPage, searchText, pag.pageSize, monthYearFilter);
  };

  const [playersPagination, setPlayersPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [confirmationFilter, setConfirmationFilter] = useState('');

  const fetchGamePlayers = async (gameId, page = 1, confirmation = '') => {
    setLoadingPlayers(true);
    try {
      const response = await api.get(`/game-players/game/${gameId}`, {
        params: { page, limit: playersPagination.pageSize, confirmation }
      });
      setGamePlayers(response.data.data || []);
      setPlayersPagination(prev => ({ ...prev, total: response.data.total, current: page }));
    } catch (error) {
      console.error('Erro ao carregar jogadores do jogo:', error);
      setGamePlayers([]);
    }
    setLoadingPlayers(false);
  };

  const handleRowClick = (record) => {
    setSelectedGame(record);
    setConfirmationFilter('');
    setPlayersPagination(prev => ({ ...prev, current: 1 }));
    fetchGamePlayers(record.id, 1, '');
  };

  const handlePlayersTableChange = (pag) => {
    fetchGamePlayers(selectedGame.id, pag.current, confirmationFilter);
  };

  const handleConfirmationFilter = (value) => {
    setConfirmationFilter(value);
    setPlayersPagination(prev => ({ ...prev, current: 1 }));
    fetchGamePlayers(selectedGame.id, 1, value);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchGames(1, value, pagination.pageSize, monthYearFilter);
  };

  const handleMonthYearChange = (value) => {
    setMonthYearFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchGames(1, searchText, pagination.pageSize, value);
  };

  const showModal = (game = null) => {
    setEditingGame(game);
    setModalVisible(true);
    if (game) {
      const formData = { ...game };
      if (game.date_of_game) {
        formData.date_of_game = dayjs(game.date_of_game);
      }
      form.setFieldsValue(formData);
    } else {
      form.resetFields();
    }
  };

  const handleSubmit = async (values) => {
    try {
      const submitData = { ...values };
      if (values.date_of_game) {
        submitData.date_of_game = values.date_of_game.format('YYYY-MM-DD');
      }

      if (editingGame) {
        await api.put(`/games/${editingGame.id}`, submitData);
        message.success('Jogo atualizado com sucesso!');
      } else {
        await api.post('/games', submitData);
        message.success('Jogo criado com sucesso!');
      }
      setModalVisible(false);
      fetchGames(pagination.current, searchText, pagination.pageSize, monthYearFilter);
    } catch (error) {
      message.error('Erro ao salvar jogo');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/games/${id}`);
      message.success('Jogo deletado com sucesso!');
      fetchGames(pagination.current, searchText, pagination.pageSize, monthYearFilter);
    } catch (error) {
      message.error('Erro ao deletar jogo');
    }
  };

  const handleImpersonate = async (customerId, gameName) => {
    Modal.confirm({
      title: 'Acessar como Organizador',
      content: `Deseja acessar o sistema como organizador do time "${gameName}"?`,
      okText: 'Sim',
      cancelText: 'Não',
      onOk: async () => {
        try {
          const response = await api.post('/auth/impersonate', { customerId });

          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));

          message.success(`Agora você está acessando como organizador de ${gameName}`);
          window.location.href = '/team-config';
        } catch (error) {
          message.error('Erro ao acessar como organizador: ' + error.response?.data?.message || 'Verifique os dados do organizador');
        }
      }
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, sorter: (a, b) => a.id - b.id },
    { title: 'Nome', dataIndex: 'name', key: 'name', render: (value) => value && value !== 'undefined' && !value.includes('NaN') ? value : '', sorter: (a, b) => a.name?.localeCompare(b.name) },
    { title: 'Data', dataIndex: 'date_of_game', key: 'date_of_game', render: (value) => value && value !== 'null' && !value.includes('NaN') ? value : '', sorter: (a, b) => {
      const aTime = parseGameDate(a.date_of_game);
      const bTime = parseGameDate(b.date_of_game);
      if (aTime === null && bTime === null) return 0;
      if (aTime === null) return -1;
      if (bTime === null) return 1;
      return aTime - bTime;
    } },
    { title: 'Jogadores', dataIndex: 'players', key: 'players', sorter: (a, b) => a.players - b.players },
    { title: 'Goleiros', dataIndex: 'goalkeepers', key: 'goalkeepers', sorter: (a, b) => a.goalkeepers - b.goalkeepers },
    { title: 'Time', dataIndex: 'customer_name', key: 'customer_name', sorter: (a, b) => a.customer_name?.localeCompare(b.customer_name) },
    { title: 'Status', dataIndex: 'status', key: 'status', sorter: (a, b) => a.status?.localeCompare(b.status) },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {user?.profile === 'ADMINISTRADOR' && (
            <Button
              icon={<UserSwitchOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleImpersonate(record.customer_id, record.customer_name);
              }}
              size="small"
              type="default"
              title="Acessar como organizador"
            />
          )}
          <Button
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              showModal(record);
            }}
            size="small"
          />
          <Popconfirm
            title="Tem certeza que deseja deletar?"
            onConfirm={(e) => {
              e.stopPropagation();
              handleDelete(record.id);
            }}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 0,
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <Search
          className={mobileSearchClass}
          placeholder="Buscar jogos..."
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={handleSearch}
          style={{ width: isMobile ? '100%' : 300 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontWeight: 'bold' }}>Filtro:</label>
          <DatePicker
            picker="month"
            format="MM/YYYY"
            allowClear
            onChange={handleMonthYearChange}
            value={monthYearFilter}
            style={{ width: isMobile ? '100%' : 160 }}
            placeholder="Mes/Ano"
          />
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()} size={isMobile ? 'large' : 'middle'}>
          Novo Jogo
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={games}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        showSorterTooltip={false}
        size={selectedGame ? 'small' : 'default'}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
      />

      {selectedGame && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3>Jogadores Confirmados - {selectedGame.name && selectedGame.name !== 'undefined' && !selectedGame.name.includes('NaN') ? selectedGame.name : 'Jogo sem nome'}</h3>
            <div>
              <label style={{ marginRight: 8, fontWeight: 'bold' }}>Filtrar por:</label>
              <Select
                placeholder="Escolha o filtro"
                allowClear
                style={{ width: 200 }}
                value={confirmationFilter}
                onChange={handleConfirmationFilter}
              >
              <Option value="vai_jogar">Vai jogar</Option>
              <Option value="nao_vai_jogar">Não vai jogar</Option>
              <Option value="espera">Ficar na espera</Option>
            </Select>
            </div>
          </div>
          <Table
            columns={[
              { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
              { title: 'Nome', dataIndex: 'name', key: 'name', render: (value) => value || '' },
              { title: 'Telefone', dataIndex: 'phone_number', key: 'phone_number' },
              { title: 'Posição', dataIndex: 'position', key: 'position', render: (value) => value == 1 ? 'Jogador de linha' : value == 2 ? 'Goleiro' : '' },
              { title: 'Tipo', dataIndex: 'type', key: 'type', render: (value) => value == 1 ? 'Mensalista' : value == 2 ? 'Avulso' : '' },
              { title: 'Confirmação', dataIndex: 'play', key: 'confirmation', render: (value, record) => {
                if (record.waiting_list) return 'Vai ficar na espera';
                return value ? 'Vai jogar' : 'Não vai jogar';
              } },
              { title: 'Atualizado em', dataIndex: 'updated_at', key: 'updated_at', render: (value) => value ? new Date(value).toLocaleString('pt-BR') : '' },
            ]}
            dataSource={gamePlayers}
            rowKey="id"
            loading={loadingPlayers}
            pagination={playersPagination}
            onChange={handlePlayersTableChange}
            size="small"
            locale={{ emptyText: 'Nenhum jogador confirmado' }}
          />
        </div>
      )}

      <Modal
        title={editingGame ? 'Editar Jogo' : 'Novo Jogo'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={isMobile ? '95vw' : 600}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item name="name" label="Nome do Jogo" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="date_of_game" label="Data do Jogo" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="customer_id" label="Time" rules={[{ required: true }]}>
            <Select placeholder="Selecione um time">
              {customers.map(customer => (
                <Option key={customer.id} value={customer.id}>
                  {customer.game_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="players" label="Número de Jogadores" rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>
          <Form.Item name="goalkeepers" label="Número de Goleiros" rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>
          {editingGame && (
            <Form.Item name="status" label="Status">
              <Select>
                <Option value="ABERTO">Aberto</Option>
                <Option value="FECHADO">Fechado</Option>
                <Option value="CANCELADO">Cancelado</Option>
              </Select>
            </Form.Item>
          )}
          <Form.Item style={{marginTop: 28}}>
            <Space>
              <Button type="primary" htmlType="submit">
                Salvar
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Games;